import React, { useState, useEffect } from "react";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen } from "Services/Listen";
import { getUserData } from "Services/API";

import { Container, Word, Box } from "../shared";

import "./SoundOut.scss";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";

import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";

import icon from "./soundout-icon.png";
import { ipaToAmirabet } from "../../../app/services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function SoundOutValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.sound_out;

  const { id } = useVersion(props.wordMetadata, {
    1: ["GRAPHEMIC_BREAKDOWN", "PHON"],
    2: ["GRAPHEMIC_BREAKDOWN", "PHON"],
    3: ["GRAPHEMIC_BREAKDOWN", "PHON"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: SoundOut1,
    2: SoundOut2,
    3: SoundOut3,
  }[versionId];

  return <Version {...props} close={close} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function displayGrapheme(grapheme, graphemeIndex, word){
  const apostrophePosition = word.indexOf("'");
  return `${apostrophePosition === graphemeIndex ? "'" : ""}${grapheme}`;
}

function SoundOut1(props) {
  const { word, wordMetadata, speech } = props;
  const { GRAPHEMIC_BREAKDOWN, PHON } = wordMetadata;
  const { speak, speakPhonemes, listenWord, listenPhoneme } = useConversation(
    speech,
    listen
  );
  const { cancellablePromise } = useCancellablePromise();

  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(0);
  const [graphemeIndex, setGraphemeIndex] = useState(0);

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1:
          await cancellablePromise(speakPhonemes({ phon: PHON, onSpeak: setGraphemeIndex }));
          await cancellablePromise(sleep());
          setGraphemeIndex(-1);
          await cancellablePromise(speak(ttsStrings.ask_sounds));
          for (let i = 0; i < graphemes.length; i++) {
            setGraphemeIndex(i);
            const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] }));
            if (status === "skip") return props.close();
          }
          setStage(2);
          break;
        case 2:
          await cancellablePromise(speak(ttsStrings.ask_word));
          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      <div style={{ marginBottom: 30 }}>{renderContent()}</div>
      <ListeningStatus />
    </Container>
  );

  function renderContent() {
    if ([0, 2].includes(stage)) {
      return (
        <div>
          {graphemes.map((g, i) => (
            <Word key={i} className="so-grapheme">
              {displayGrapheme(g, i, word)}
            </Word>
          ))}
        </div>
      );
    }
    if (stage === 1) {
      return (
        <div>
          {graphemes.map((g, i) => {
            const isDisabled = graphemeIndex !== i;
            return (
              <Word key={i} disabled={isDisabled} className="so-grapheme">
                {displayGrapheme(g, i, word)}
              </Word>
            );
          })}
        </div>
      );
    }
  }
}

function SoundOut2(props) {
  const { word, wordMetadata, speech } = props;
  const { GRAPHEMIC_BREAKDOWN, PHON } = wordMetadata;
  const { speak, speakPhonemes, listenWord, listenPhoneme } = useConversation(
    speech,
    listen
  );
  const { cancellablePromise } = useCancellablePromise();

  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(0);
  const [graphemeIndex, setGraphemeIndex] = useState(0);

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1:
          await cancellablePromise(speakPhonemes({ phon: PHON, onSpeak: setGraphemeIndex }));
          await cancellablePromise(sleep());
          await cancellablePromise(speak(ttsStrings.ask_sounds));
          setGraphemeIndex(0);
          for (let i = 0; i < graphemes.length; i++) {
            setGraphemeIndex(i);
            const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] }));
            if (status === "skip") return props.close();
          }
          setStage(2);
          break;
        case 2:
          await cancellablePromise(speak(ttsStrings.ask_word));
          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      <div style={{ marginBottom: 30 }}>{renderContent()}</div>
      <ListeningStatus />
    </Container>
  );

  function renderContent() {
    return (
      <div>
        {graphemes.map((g, i) => {
          const isHidden = i > graphemeIndex;
          return (
            <Word key={i} hidden={isHidden} className="so-grapheme">
              {displayGrapheme(g, i, word)}
            </Word>
          );
        })}
      </div>
    );
  }
}

function SoundOut3(props) {
  const { word, wordMetadata, speech } = props;
  const { GRAPHEMIC_BREAKDOWN, PHON } = wordMetadata;
  const { speak, speakPhonemes, listenWord, listenPhoneme } = useConversation(
    speech,
    listen
  );
  const { cancellablePromise } = useCancellablePromise();

  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(0);
  const [graphemeIndex, setGraphemeIndex] = useState(0);

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1:
          await cancellablePromise(speakPhonemes({ phon: PHON, onSpeak: setGraphemeIndex }));
          await cancellablePromise(sleep());
          await cancellablePromise(speak(ttsStrings.ask_sounds));
          setGraphemeIndex(0);
          setStage(2);
          break;
        case 2:
          for (let i = 0; i < graphemes.length; i++) {
            setGraphemeIndex(i);
            const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] }));
            if (status === "skip") return props.close();
          }
          setStage(3);
          break;
        case 3:
          await cancellablePromise(speak(ttsStrings.ask_word));
          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      <div style={{ marginBottom: 30 }}>{renderContent()}</div>
      <ListeningStatus />
    </Container>
  );

  function renderContent() {
    return (
      <div>
        {graphemes.map((g, i) => {
          let isDisabled = false;
          if (stage > 1) isDisabled = i > graphemeIndex;
          else isDisabled = i !== graphemeIndex;
          return (
            <Box key={i}>
              <Word disabled={isDisabled} className="so-grapheme">
                {displayGrapheme(g, i, word)}
              </Word>
            </Box>
          );
        })}
      </div>
    );
  }
}
